/**
 * PublicShareStatus
 *
 * Status surfaces for the non-ready states of a public share. The `loading`
 * state stays a centered, shell-less spinner. The fatal states (notFound /
 * revoked / error) render the branded dual-column layout — a "lost route" map
 * illustration on the left and an explanatory message on the right — matching
 * the password gate and the onboarding / invitation screens.
 *
 * @module components/share/public/PublicShareStatus
 */
import { memo, type ComponentType } from 'react';
import { Loader, Stack, Text } from '@mantine/core';
import {
  IconAlertTriangle,
  IconLinkOff,
  IconMapPinOff,
  type IconProps,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { SplitScreenLayout } from '@/components/layout/SplitScreenLayout';
import { GateMobileLayout } from '@/components/layout/GateMobileLayout';
import { OnboardingSettingsBar } from '@/components/layout/OnboardingSettingsBar';
import { RouteErrorIllustration } from '@/components/common/illustrations/RouteErrorIllustration';
import { useDeviceLayout } from '@/hooks';
import { ICON_SIZE_EMPTY_STATE } from '@/constants/ui';

import { ShareGateLeftPanel } from '../shareGate';
import styles from './PublicShareStatus.module.css';

type StatusVariant = 'loading' | 'notFound' | 'revoked' | 'error';
type FatalVariant = Exclude<StatusVariant, 'loading'>;

interface PublicShareStatusProps {
  variant: StatusVariant;
}

interface FatalCopy {
  Icon: ComponentType<IconProps>;
  title: string;
  description: string;
}

export const PublicShareStatus = memo(function PublicShareStatus({
  variant,
}: PublicShareStatusProps) {
  const { t } = useTranslation();
  const { isMobile } = useDeviceLayout();

  if (variant === 'loading') {
    return (
      <div className={styles.wrapper}>
        <Stack align="center" gap="sm">
          <Loader />
          <Text c="dimmed">{t('share.public.loading')}</Text>
        </Stack>
      </div>
    );
  }

  // Literal i18n keys per fatal variant (no runtime-assembled keys).
  const COPY: Record<FatalVariant, FatalCopy> = {
    notFound: {
      Icon: IconMapPinOff,
      title: t('share.public.notFound'),
      description: t('share.public.notFoundDescription'),
    },
    revoked: {
      Icon: IconLinkOff,
      title: t('share.public.revoked'),
      description: t('share.public.revokedDescription'),
    },
    error: {
      Icon: IconAlertTriangle,
      title: t('share.public.loadError'),
      description: t('share.public.loadErrorDescription'),
    },
  };

  const { Icon, title, description } = COPY[variant];

  const messageContent = (
    <div className={styles.rightMessageWrapper}>
      <Stack gap="xs">
        <Icon
          size={ICON_SIZE_EMPTY_STATE}
          className={styles.statusIcon}
          aria-hidden
        />
        <h2 className={styles.statusTitle}>{title}</h2>
        <Text c="dimmed">{description}</Text>
      </Stack>
    </div>
  );

  if (isMobile) {
    return <GateMobileLayout>{messageContent}</GateMobileLayout>;
  }

  return (
    <SplitScreenLayout
      leftPanelBackground={<RouteErrorIllustration />}
      leftPanel={<ShareGateLeftPanel variant="error" />}
      settingsBar={<OnboardingSettingsBar />}
      rightPanel={messageContent}
    />
  );
});
